/**
 * The dashboard and every report.
 *
 * Each figure comes from the server, worked out from the transactions themselves.
 * Excel export is generated server-side; PDF is the browser's own print-to-PDF,
 * which is the only way Arabic comes out correctly shaped in a PDF without
 * shipping a font engine.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Permission } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useToast } from '../../core/state/toast.js';
import { useAuth } from '../../core/state/auth.js';
import { api, download } from '../../core/api.js';
import { escapeHtml, printDocument } from '../../core/print.js';
import { Button, Empty, Loading, Pill, Tabs } from '../../core/ui/ui.js';
import { BarChart, BarList, Kpi, RangePicker, useDateRange } from '../../core/ui/bits.js';

interface Summary {
  grossSales: number; discounts: number; returns: number; netSales: number;
  vat: number; netExVat: number; rounding: number; transactions: number;
  itemsSold: number; averageSale: number; itemsPerSale: number;
  costOfGoods: number; grossProfit: number; marginPercent: number;
}

interface Row {
  key: string; labelEn: string; labelAr: string;
  qty: number; net: number; cost: number; profit: number; transactions: number;
}

// ---------------------------------------------------------------------------
// Dashboard - the first thing the owner sees on a phone
// ---------------------------------------------------------------------------

export function Dashboard() {
  const t = useT();
  const { lang } = useI18n();
  const { money, num, settings } = useSettings();
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api<Record<string, unknown>>('/reports/dashboard')
      .then(setData)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!data) return <Empty icon="📶" title={t('net.needOnline')} />;

  const today = data.today as Summary;
  const change = data.changePercent as number | null;
  const top = (data.topToday as Row[]) ?? [];
  const low = (data.lowStock as Record<string, unknown>[]) ?? [];
  const cash = (data.cashInDrawer as Record<string, number>) ?? {};
  const hourly = (data.hourly as Row[]) ?? [];

  return (
    <div className="col">
      <div className="grid c4">
        <Kpi
          label={t('dash.todaySales')}
          value={money(today.netSales)}
          accent
          detail={
            change === null
              ? undefined
              : `${change > 0 ? '▲' : change < 0 ? '▼' : '='} ${Math.abs(change)}% ${t('reports.vsLastWeek')}`
          }
          trend={change === null ? undefined : change >= 0 ? 'up' : 'down'}
        />
        <Kpi label={t('reports.transactions')} value={num(today.transactions)} detail={`${t('reports.averageSale')} ${money(today.averageSale)}`} />
        {settings.shiftsEnabled ? (
          <Kpi
            label={t('dash.cashInDrawer')}
            value={money(cash.ILS ?? 0)}
            detail={Object.entries(cash)
              .filter(([c]) => c !== 'ILS')
              .map(([c, v]) => `${c} ${(v / 100).toFixed(2)}`)
              .join(' · ')}
          />
        ) : null}
        <Kpi label={t('dash.stockValue')} value={money(Number(data.inventoryAtCost))} detail={`${t('credits.outstanding')} ${money(Number(data.creditOutstanding))}`} />
      </div>

      {Number(data.lowStockCount) > 0 ? (
        <div className="note-warn">{t('dash.lowStockAlert', { n: Number(data.lowStockCount) })}</div>
      ) : null}

      <div className="grid c2" style={{ alignItems: 'start' }}>
        <div className="card">
          <h3>{t('dash.hourly')}</h3>
          {hourly.length === 0 ? (
            <p className="muted small">{t('dash.noSalesYet')}</p>
          ) : (
            <BarChart kind="hour" data={hourly.map((h) => ({ label: String(h.key), value: h.net }))} format={(v) => money(v)} />
          )}
        </div>

        <div className="card">
          <h3>{t('dash.topToday')}</h3>
          {top.length === 0 ? (
            <p className="muted small">{t('dash.noSalesYet')}</p>
          ) : (
            <BarList
              rows={top.map((r) => ({
                label: lang === 'ar' ? r.labelAr : r.labelEn,
                value: r.net,
                sub: `${r.qty}`,
              }))}
              format={money}
            />
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>{t('stock.lowStock')}</h3>
          <div className="spacer" />
          <Pill kind="warn">{Number(data.lowStockCount)}</Pill>
        </div>
        {low.length === 0 ? (
          <p className="muted small">{t('stock.noDifferences')}</p>
        ) : (
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.product')}</th>
                  <th>{t('common.size')}</th>
                  <th className="num">{t('common.stock')}</th>
                </tr>
              </thead>
              <tbody>
                {low.map((r) => (
                  <tr key={String(r.id)}>
                    <td className="strong" data-label={t('common.product')}>
                      {lang === 'ar' ? String(r.name_ar) : String(r.name_en)}
                      <span className="xsmall muted"> {lang === 'ar' ? String(r.color_ar ?? '') : String(r.color_en ?? '')}</span>
                    </td>
                    <td data-label={t('common.size')}>{String(r.size)}</td>
                    <td className="num" data-label={t('common.stock')}>
                      <Pill kind={Number(r.stock) <= 0 ? 'danger' : 'warn'}>{Number(r.stock)}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

type Tab = 'sales' | 'profit' | 'vat' | 'cash' | 'exchanges' | 'inventory' | 'stock' | 'purchases' | 'pl';

export function Reports() {
  const t = useT();
  const { can } = useAuth();
  const { settings } = useSettings();
  const [tab, setTab] = useState<Tab>('sales');
  const { range, setRange } = useDateRange('month');

  // Profit and cost are their own permission - a shop may well want a
  // supervisor who can see what sold without seeing what it cost. A tab that
  // would only produce a refusal is not shown at all.
  const tabs = useMemo(
    () =>
      ([
        { key: 'sales', label: t('reports.sales'), need: 'reports.sales' },
        { key: 'profit', label: t('reports.profit'), need: 'reports.profit' },
        { key: 'vat', label: t('reports.vat'), need: 'reports.vat' },
        ...(settings.shiftsEnabled ? [{ key: 'cash', label: t('reports.cash'), need: 'reports.cash' }] : []),
        { key: 'exchanges', label: t('reports.exchanges'), need: 'reports.sales' },
        { key: 'inventory', label: t('reports.inventory'), need: 'reports.inventory' },
        { key: 'stock', label: t('reports.stock'), need: 'stock.view' },
        { key: 'purchases', label: t('reports.purchases'), need: 'purchase.view' },
        { key: 'pl', label: t('reports.pl'), need: 'reports.profit' },
      ] as { key: Tab; label: string; need: Permission }[]).filter((x) => can(x.need)),
    [t, can, settings.shiftsEnabled],
  );

  // If the tab they are on is not one of theirs, move to the first that is.
  useEffect(() => {
    if (tabs.length > 0 && !tabs.some((x) => x.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab]);

  if (tabs.length === 0) return <Empty icon="🔒" title={t('error.notAllowed')} hint={t('roles.askOwner')} />;

  return (
    <div className="col">
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab !== 'inventory' && tab !== 'stock' ? (
        <div className="row wrap no-print">
          <RangePicker range={range} onChange={setRange} />
        </div>
      ) : null}

      {tab === 'sales' ? <SalesReport range={range} /> : null}
      {tab === 'profit' ? <ProfitReport range={range} /> : null}
      {tab === 'vat' ? <VatReport range={range} /> : null}
      {tab === 'cash' ? <CashReport range={range} /> : null}
      {tab === 'exchanges' ? <ExchangeReport range={range} /> : null}
      {tab === 'inventory' ? <InventoryReport /> : null}
      {tab === 'stock' ? <StockReports /> : null}
      {tab === 'purchases' ? <PurchaseReport range={range} /> : null}
      {tab === 'pl' ? <PlReport range={range} /> : null}
    </div>
  );
}

/** Every report uses the same shape, so the fetching lives in one hook. */
function useReport<T>(path: string, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    api<T>(path)
      .then((d) => alive && setData(d))
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, failed };
}

function ExportBar({ xlsx, onPrint }: { xlsx?: string; onPrint?: () => void }) {
  const t = useT();
  const { can } = useAuth();
  const toast = useToast();
  return (
    <div className="row no-print" style={{ justifyContent: 'flex-end' }}>
      {onPrint ? <Button size="sm" onClick={onPrint}>{t('common.exportPdf')}</Button> : null}
      {xlsx && can('reports.export') ? (
        <Button
          size="sm"
          onClick={() =>
            void download(xlsx, 'report.xlsx').catch(() => toast.err(t('net.needOnline')))
          }
        >
          {t('common.export')}
        </Button>
      ) : null}
    </div>
  );
}

function RowTable({ rows, title }: { rows: Row[]; title: string }) {
  const t = useT();
  const { lang } = useI18n();
  const { money, num } = useSettings();
  if (rows.length === 0) return null;
  return (
    <div className="card flush">
      <div style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
        <h3>{title}</h3>
      </div>
      <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="cards">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th className="num">{t('common.qty')}</th>
              <th className="num">{t('reports.netSales')}</th>
              <th className="num">{t('common.profit')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="strong" data-label={t('common.name')}>{lang === 'ar' ? r.labelAr : r.labelEn}</td>
                <td className="num tnum" data-label={t('common.qty')}>{num(r.qty)}</td>
                <td className="num tnum" data-label={t('reports.netSales')}>{money(r.net)}</td>
                <td className="num tnum" data-label={t('common.profit')}>{r.cost ? money(r.profit) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalesReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { lang } = useI18n();
  const { money, num, settings } = useSettings();
  const { data, loading, failed } = useReport<{
    summary: Summary;
    byDay: Row[]; byHour: Row[]; byCategory: Row[]; byBrand: Row[]; byProduct: Row[];
    bySize: Row[]; byColor: Row[]; byCashier: Row[]; byPayment: Row[]; byCurrency: Row[];
  }>(`/reports/sales?from=${range.from}&to=${range.to}`, [range.from, range.to]);

  const printIt = () => {
    if (!data) return;
    const s = data.summary;
    const rows = (list: Row[], title: string) =>
      list.length
        ? `<h3>${escapeHtml(title)}</h3><table><thead><tr><th>${escapeHtml(t('common.name'))}</th><th class="num">${escapeHtml(t('common.qty'))}</th><th class="num">${escapeHtml(t('reports.netSales'))}</th></tr></thead><tbody>${list
            .map(
              (r) =>
                `<tr><td>${escapeHtml(lang === 'ar' ? r.labelAr : r.labelEn)}</td><td class="num">${r.qty}</td><td class="num">${escapeHtml(money(r.net))}</td></tr>`,
            )
            .join('')}</tbody></table>`
        : '';

    void printDocument(
      t('reports.sales'),
      `<h1>${escapeHtml(t('reports.sales'))}</h1>
       <div class="sub">${escapeHtml(settings.storeNameEn)} · ${escapeHtml(range.from.slice(0, 10))} → ${escapeHtml(range.to.slice(0, 10))}</div>
       <div class="cards">
         <div class="kpi"><div class="k">${escapeHtml(t('reports.netSales'))}</div><div class="v">${escapeHtml(money(s.netSales))}</div></div>
         <div class="kpi"><div class="k">${escapeHtml(t('reports.vatCollected'))}</div><div class="v">${escapeHtml(money(s.vat))}</div></div>
         <div class="kpi"><div class="k">${escapeHtml(t('reports.transactions'))}</div><div class="v">${s.transactions}</div></div>
         <div class="kpi"><div class="k">${escapeHtml(t('reports.grossProfit'))}</div><div class="v">${escapeHtml(money(s.grossProfit))}</div></div>
       </div>
       ${rows(data.byDay, t('reports.byDay'))}
       ${rows(data.byCategory, t('reports.byCategory'))}
       ${rows(data.bySize, t('reports.bySize'))}
       ${rows(data.byPayment, t('reports.byPayment'))}
       <div class="foot">${escapeHtml(new Date().toLocaleString('en-GB'))}</div>`,
    );
  };

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;
  if (data.summary.transactions === 0) return <Empty icon="📊" title={t('reports.noData')} />;

  const s = data.summary;

  return (
    <div className="col">
      <ExportBar xlsx={`/reports/export/sales.xlsx?from=${range.from}&to=${range.to}`} onPrint={printIt} />

      <div className="grid c4">
        <Kpi label={t('reports.netSales')} value={money(s.netSales)} accent />
        <Kpi label={t('reports.grossSales')} value={money(s.grossSales)} detail={`${t('reports.discounts')} −${money(s.discounts)}`} />
        <Kpi label={t('reports.vatCollected')} value={money(s.vat)} detail={`${t('reports.netExVat')} ${money(s.netExVat)}`} />
        <Kpi label={t('reports.grossProfit')} value={money(s.grossProfit)} detail={`${t('reports.margin')} ${s.marginPercent}%`} />
        <Kpi label={t('reports.transactions')} value={num(s.transactions)} />
        <Kpi label={t('reports.averageSale')} value={money(s.averageSale)} />
        <Kpi label={t('reports.itemsSold')} value={num(s.itemsSold)} detail={`${t('reports.itemsPerSale')} ${s.itemsPerSale}`} />
        <Kpi label={t('reports.returns')} value={money(s.returns)} detail={`${t('reports.rounding')} ${money(s.rounding)}`} />
      </div>

      <div className="card">
        <h3>{t('reports.byDay')}</h3>
        <BarChart kind="day" from={range.from} to={range.to} data={data.byDay.map((d) => ({ label: d.key, value: d.net }))} format={money} height={170} />
      </div>

      <div className="grid c2" style={{ alignItems: 'start' }}>
        <div className="card">
          <h3>{t('reports.byHour')}</h3>
          <BarChart kind="hour" data={data.byHour.map((d) => ({ label: String(d.key), value: d.net }))} format={money} />
        </div>
        <div className="card">
          <h3>{t('reports.byPayment')}</h3>
          <BarList rows={data.byPayment.map((r) => ({ label: lang === 'ar' ? r.labelAr || r.labelEn : r.labelEn, value: r.net }))} format={money} />
          <h3 style={{ marginBlockStart: 'var(--s-5)' }}>{t('reports.byCurrency')}</h3>
          <BarList rows={data.byCurrency.map((r) => ({ label: lang === 'ar' ? r.labelAr || r.labelEn : r.labelEn, value: r.net }))} format={money} />
        </div>
      </div>

      <div className="grid c2" style={{ alignItems: 'start' }}>
        <RowTable rows={data.byCategory} title={t('reports.byCategory')} />
        <RowTable rows={data.byBrand} title={t('reports.byBrand')} />
        <RowTable rows={data.bySize} title={t('reports.bySize')} />
        <RowTable rows={data.byColor} title={t('reports.byColour')} />
        <RowTable rows={data.byCashier} title={t('reports.byCashier')} />
        <RowTable rows={data.byProduct} title={t('reports.byProduct')} />
      </div>
    </div>
  );
}

function ProfitReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { money } = useSettings();
  const { data, loading, failed } = useReport<{ summary: Summary; byProduct: Row[]; byCategory: Row[]; byDay: Row[] }>(
    `/reports/profit?from=${range.from}&to=${range.to}`,
    [range.from, range.to],
  );

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;
  const s = data.summary;

  return (
    <div className="col">
      <div className="grid c4">
        <Kpi label={t('reports.netExVat')} value={money(s.netExVat)} detail={`${t('reports.netSales')} ${money(s.netSales)}`} />
        <Kpi label={t('reports.cogs')} value={money(s.costOfGoods)} />
        <Kpi label={t('reports.grossProfit')} value={money(s.grossProfit)} accent />
        <Kpi label={t('reports.margin')} value={`${s.marginPercent}%`} />
      </div>
      <p className="small muted">{t('reports.profitNote')}</p>
      <div className="card">
        <h3>{t('reports.byDay')}</h3>
        <BarChart kind="day" from={range.from} to={range.to} data={data.byDay.map((d) => ({ label: d.key, value: d.profit }))} format={money} height={160} />
      </div>
      <div className="grid c2" style={{ alignItems: 'start' }}>
        <RowTable rows={data.byCategory} title={t('reports.byCategory')} />
        <RowTable rows={data.byProduct.slice(0, 60)} title={t('reports.byProduct')} />
      </div>
    </div>
  );
}

function VatReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { money, settings } = useSettings();
  const { data, loading, failed } = useReport<{
    outputVat: number; netSales: number; inputVat: number; netPurchases: number;
    expenseVat: number; payable: number; rate: number;
  }>(`/reports/vat?from=${range.from}&to=${range.to}`, [range.from, range.to]);

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;

  /*
   * The VAT the shop paid comes from two places - stock bought from suppliers
   * and running costs like rent and bills. Showing only the total under the
   * word "purchases" made a month with no deliveries read as
   * "VAT on purchases 444.13, Purchases 0.00". Both parts are named here, so
   * the total is visibly the sum of them.
   */
  const purchaseVat = data.inputVat - data.expenseVat;

  const rows: [string, number][] = [
    [t('reports.netExVat'), data.netSales],
    [t('reports.outputVat'), data.outputVat],
    [t('reports.netPurchasesExVat'), data.netPurchases],
    [t('reports.purchaseVat'), purchaseVat],
    [t('reports.expenseVat'), data.expenseVat],
    [t('reports.inputVat'), data.inputVat],
    [t('reports.netVat'), data.payable],
  ];

  const printIt = () =>
    void printDocument(
      t('reports.vat'),
      `<h1>${escapeHtml(t('reports.vat'))}</h1>
       <div class="sub">${escapeHtml(settings.storeNameEn)} · ${escapeHtml(settings.taxNumber)} · ${escapeHtml(range.from.slice(0, 10))} → ${escapeHtml(range.to.slice(0, 10))}</div>
       <table><tbody>${rows
         .map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td class="num">${escapeHtml(money(v))}</td></tr>`)
         .join('')}</tbody></table>
       <div class="foot">${escapeHtml(t('reports.vatNote'))}</div>`,
    );

  return (
    <div className="col">
      <ExportBar xlsx={`/reports/export/vat.xlsx?from=${range.from}&to=${range.to}`} onPrint={printIt} />
      <div className="grid c3">
        <Kpi label={t('reports.outputVat')} value={money(data.outputVat)} detail={`${t('reports.netExVat')} ${money(data.netSales)}`} />
        <Kpi
          label={t('reports.inputVat')}
          value={money(data.inputVat)}
          detail={`${t('reports.purchases')} ${money(purchaseVat)} · ${t('reports.expenses')} ${money(data.expenseVat)}`}
        />
        <Kpi label={t('reports.netVat')} value={money(data.payable)} accent />
      </div>
      <div className="card">
        <p className="small muted">{t('reports.vatNote')}</p>
        <div className="table-wrap">
          <table>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k}>
                  <td className="strong">{k}</td>
                  <td className="num tnum">{money(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CashReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { money, amount, dateTime } = useSettings();
  const { data, loading, failed } = useReport<
    {
      shiftId: string; userName: string; deviceName: string; openedAt: string; closedAt: string | null;
      status: string; netSales: number; transactions: number; cashIls: number; cardIls: number; otherIls: number;
      currencies: { code: string; opening: number; takings: number; expected: number; counted: number | null; difference: number | null }[];
    }[]
  >(`/reports/cash?from=${range.from}&to=${range.to}`, [range.from, range.to]);

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;
  if (data.length === 0) return <Empty icon="💵" title={t('reports.noData')} />;

  return (
    <div className="col">
      {data.map((s) => (
        <div className="card" key={s.shiftId}>
          <div className="card-head">
            <div>
              <h3>{s.userName} · {s.deviceName}</h3>
              <div className="xsmall muted">
                {dateTime(s.openedAt)} → {s.closedAt ? dateTime(s.closedAt) : '—'}
              </div>
            </div>
            <div className="spacer" />
            <Pill kind={s.status === 'open' ? 'warn' : 'ok'}>{s.status}</Pill>
          </div>

          <div className="grid c4">
            <Kpi label={t('reports.netSales')} value={money(s.netSales)} />
            <Kpi label={t('reports.transactions')} value={String(s.transactions)} />
            <Kpi label={t('pay.cash')} value={money(s.cashIls)} />
            <Kpi label={t('pay.card')} value={money(s.cardIls)} />
          </div>

          <div className="table-wrap" style={{ marginBlockStart: 'var(--s-4)' }}>
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.currency')}</th>
                  <th className="num">{t('shift.openingCash')}</th>
                  <th className="num">{t('shift.takings')}</th>
                  <th className="num">{t('reports.expected')}</th>
                  <th className="num">{t('reports.counted')}</th>
                  <th className="num">{t('reports.overShort')}</th>
                </tr>
              </thead>
              <tbody>
                {s.currencies.map((c) => (
                  <tr key={c.code}>
                    <td className="strong" data-label={t('common.currency')}>{c.code}</td>
                    <td className="num tnum" data-label={t('shift.openingCash')}>{amount(c.opening, c.code)}</td>
                    <td className="num tnum" data-label={t('shift.takings')}>{amount(c.takings, c.code)}</td>
                    <td className="num tnum" data-label={t('reports.expected')}>{amount(c.expected, c.code)}</td>
                    <td className="num tnum" data-label={t('reports.counted')}>
                      {c.counted === null ? '—' : amount(c.counted, c.code)}
                    </td>
                    <td className="num" data-label={t('reports.overShort')}>
                      {c.difference === null ? (
                        '—'
                      ) : c.difference === 0 ? (
                        <Pill kind="ok">{t('shift.exact')}</Pill>
                      ) : (
                        <Pill kind={c.difference > 0 ? 'warn' : 'danger'}>
                          {amount(c.difference, c.code)}
                        </Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExchangeReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { lang } = useI18n();
  const { money, num } = useSettings();
  const { data, loading, failed } = useReport<{
    transactions: number; netValue: number; returnedValue: number;
    topProducts: { en: string; ar: string; size: string; color: string; qty: number; value: number }[];
    bySize: { size: string; qty: number }[];
    creditIssued: number; creditUsed: number; creditOutstanding: number;
  }>(`/reports/exchanges?from=${range.from}&to=${range.to}`, [range.from, range.to]);

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;

  return (
    <div className="col">
      <div className="grid c4">
        <Kpi label={t('reports.exchanges')} value={num(data.transactions)} />
        <Kpi label={t('reports.returns')} value={money(data.returnedValue)} />
        <Kpi label={t('reports.creditIssued')} value={money(data.creditIssued)} detail={`${t('reports.creditUsed')} ${money(data.creditUsed)}`} />
        <Kpi label={t('credits.outstanding')} value={money(data.creditOutstanding)} accent />
      </div>

      <div className="grid c2" style={{ alignItems: 'start' }}>
        <div className="card">
          <h3>{t('reports.exchangesBySize')}</h3>
          <p className="xsmall muted">{t('reports.sizeHint')}</p>
          {data.bySize.length === 0 ? (
            <p className="muted small">{t('reports.noData')}</p>
          ) : (
            <BarList rows={data.bySize.map((s) => ({ label: s.size, value: s.qty }))} format={(v) => num(v)} />
          )}
        </div>

        <div className="card flush">
          <div style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
            <h3>{t('reports.mostExchanged')}</h3>
          </div>
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.product')}</th>
                  <th>{t('common.size')}</th>
                  <th className="num">{t('common.qty')}</th>
                </tr>
              </thead>
              <tbody>
                {data.topProducts.map((p, i) => (
                  <tr key={i}>
                    <td className="strong" data-label={t('common.product')}>{lang === 'ar' ? p.ar : p.en}</td>
                    <td data-label={t('common.size')}>{p.size}</td>
                    <td className="num tnum" data-label={t('common.qty')}>{p.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function InventoryReport() {
  const t = useT();
  const { lang } = useI18n();
  const { money, num } = useSettings();
  const { data, loading, failed } = useReport<{
    rows: { categoryId: string; labelEn: string; labelAr: string; units: number; atCost: number; atRetail: number; potentialProfit: number }[];
    totals: { units: number; atCost: number; atRetail: number; potentialProfit: number };
  }>('/reports/inventory');

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;

  return (
    <div className="col">
      <ExportBar xlsx="/reports/export/inventory.xlsx" />
      <div className="grid c4">
        <Kpi label={t('common.items')} value={num(data.totals.units)} />
        <Kpi label={t('reports.atCost')} value={money(data.totals.atCost)} accent />
        <Kpi label={t('reports.atRetail')} value={money(data.totals.atRetail)} />
        <Kpi label={t('reports.potentialProfit')} value={money(data.totals.potentialProfit)} />
      </div>
      <div className="card flush">
        <div className="table-wrap">
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.category')}</th>
                <th className="num">{t('common.items')}</th>
                <th className="num">{t('reports.atCost')}</th>
                <th className="num">{t('reports.atRetail')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.categoryId}>
                  <td className="strong" data-label={t('common.category')}>{lang === 'ar' ? r.labelAr : r.labelEn}</td>
                  <td className="num tnum" data-label={t('common.items')}>{num(r.units)}</td>
                  <td className="num tnum" data-label={t('reports.atCost')}>{money(r.atCost)}</td>
                  <td className="num tnum" data-label={t('reports.atRetail')}>{money(r.atRetail)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>{t('common.total')}</td>
                <td className="num tnum">{num(data.totals.units)}</td>
                <td className="num tnum">{money(data.totals.atCost)}</td>
                <td className="num tnum">{money(data.totals.atRetail)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function StockReports() {
  const t = useT();
  const { lang } = useI18n();
  const { money, settings, date } = useSettings();
  const low = useReport<Record<string, unknown>[]>('/reports/low-stock');
  const dead = useReport<Record<string, unknown>[]>(`/reports/dead-stock?days=${settings.deadStockDays}`);
  const best = useReport<Record<string, unknown>[]>(
    `/reports/best-sellers?from=${new Date(Date.now() - 30 * 86_400_000).toISOString()}&to=${new Date().toISOString()}`,
  );

  if (low.loading || dead.loading || best.loading) return <Loading />;

  const nm = (r: Record<string, unknown>) => (lang === 'ar' ? String(r.name_ar) : String(r.name_en));

  return (
    <div className="col">
      <div className="card flush">
        <div style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
          <h3>{t('reports.bestSellers')}</h3>
        </div>
        <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.product')}</th>
                <th>{t('common.size')}</th>
                <th className="num">{t('common.qty')}</th>
                <th className="num">{t('reports.netSales')}</th>
              </tr>
            </thead>
            <tbody>
              {(best.data ?? []).map((r) => (
                <tr key={String(r.variant_id)}>
                  <td className="strong" data-label={t('common.product')}>{nm(r)}</td>
                  <td data-label={t('common.size')}>{String(r.size)}</td>
                  <td className="num tnum" data-label={t('common.qty')}>{Number(r.qty)}</td>
                  <td className="num tnum" data-label={t('reports.netSales')}>{money(Number(r.net))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card flush">
        <div style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
          <h3>{t('stock.lowStock')}</h3>
        </div>
        <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.product')}</th>
                <th>{t('common.size')}</th>
                <th className="num">{t('common.stock')}</th>
              </tr>
            </thead>
            <tbody>
              {(low.data ?? []).map((r) => (
                <tr key={String(r.id)}>
                  <td className="strong" data-label={t('common.product')}>{nm(r)}</td>
                  <td data-label={t('common.size')}>{String(r.size)}</td>
                  <td className="num" data-label={t('common.stock')}>
                    <Pill kind={Number(r.stock) <= 0 ? 'danger' : 'warn'}>{Number(r.stock)}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card flush">
        <div style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
          <h3>{t('reports.deadStock')}</h3>
          <p className="xsmall muted">{t('reports.deadStockNote', { days: settings.deadStockDays })}</p>
        </div>
        <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.product')}</th>
                <th>{t('common.size')}</th>
                <th className="num">{t('common.stock')}</th>
                <th>{t('reports.lastSold')}</th>
              </tr>
            </thead>
            <tbody>
              {(dead.data ?? []).map((r) => (
                <tr key={String(r.id)}>
                  <td className="strong" data-label={t('common.product')}>{nm(r)}</td>
                  <td data-label={t('common.size')}>{String(r.size)}</td>
                  <td className="num tnum" data-label={t('common.stock')}>{Number(r.stock)}</td>
                  <td data-label={t('reports.lastSold')}>
                    {r.last_sold ? date(String(r.last_sold)) : <span className="muted">{t('reports.never')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PurchaseReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { money, settings } = useSettings();
  const { data, loading, failed } = useReport<{
    bySupplier: Record<string, unknown>[];
    totalOwed: number;
  }>(`/reports/purchases?from=${range.from}&to=${range.to}`, [range.from, range.to]);

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;

  const printIt = () =>
    void printDocument(
      t('reports.purchases'),
      `<h1>${escapeHtml(t('reports.purchases'))}</h1>
       <div class="sub">${escapeHtml(settings.storeNameEn)} · ${escapeHtml(range.from.slice(0, 10))} → ${escapeHtml(range.to.slice(0, 10))}</div>
       <table><thead><tr><th>${escapeHtml(t('common.supplier'))}</th><th class="num">${escapeHtml(t('common.items'))}</th><th class="num">${escapeHtml(t('common.total'))}</th><th class="num">${escapeHtml(t('reports.owedOfThese'))}</th></tr></thead>
       <tbody>${data.bySupplier
         .map((r) => `<tr><td>${escapeHtml(String(r.name))}</td><td class="num">${Number(r.invoices)}</td><td class="num">${escapeHtml(money(Number(r.total)))}</td><td class="num">${escapeHtml(money(Number(r.owed)))}</td></tr>`)
         .join('')}</tbody></table>
       <p><strong>${escapeHtml(t('reports.owedToSuppliers'))}: ${escapeHtml(money(data.totalOwed))}</strong> — ${escapeHtml(t('reports.owedAllTime'))}</p>`,
    );

  return (
    <div className="col">
      <ExportBar onPrint={printIt} />
      <div className="grid c2">
        <Kpi
          label={t('reports.purchases')}
          value={money(data.bySupplier.reduce((a, r) => a + Number(r.total), 0))}
        />
        {/*
          * This tile is EVERY unpaid invoice, while the table below only covers
          * the dates chosen - so on a one-day range the tile shows a real debt
          * over an empty table. Both now say which they are.
          */}
        <Kpi
          label={t('reports.owedToSuppliers')}
          value={money(data.totalOwed)}
          detail={t('reports.owedAllTime')}
          accent
        />
      </div>
      <div className="card flush">
        <div className="table-wrap">
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.supplier')}</th>
                <th className="num">{t('common.items')}</th>
                <th className="num">{t('common.total')}</th>
                <th className="num">{t('reports.owedOfThese')}</th>
              </tr>
            </thead>
            <tbody>
              {data.bySupplier.map((r) => (
                <tr key={String(r.id)}>
                  <td className="strong" data-label={t('common.supplier')}>{String(r.name)}</td>
                  <td className="num" data-label={t('common.items')}>{Number(r.invoices)}</td>
                  <td className="num tnum" data-label={t('common.total')}>{money(Number(r.total))}</td>
                  <td className="num tnum" data-label={t('reports.owedOfThese')}>{money(Number(r.owed))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PlReport({ range }: { range: { from: string; to: string } }) {
  const t = useT();
  const { money, settings } = useSettings();
  const { data, loading, failed } = useReport<{
    netSales: number; netSalesExVat: number; vat: number; costOfGoods: number;
    grossProfit: number; expenses: number; expenseVat: number; expensesExVat: number;
    netProfit: number; marginPercent: number;
    expenseRows: Record<string, unknown>[];
  }>(`/reports/profit-and-loss?from=${range.from}&to=${range.to}`, [range.from, range.to]);

  if (loading) return <Loading />;
  if (failed || !data) return <Empty icon="📶" title={t('net.needOnline')} />;

  const lines: [string, number, boolean?][] = [
    [t('reports.netSales'), data.netSales],
    [t('common.vat'), -data.vat],
    [t('reports.netExVat'), data.netSalesExVat, true],
    [t('reports.cogs'), -data.costOfGoods],
    [t('reports.grossProfit'), data.grossProfit, true],
    [t('reports.expensesExVat'), -data.expensesExVat],
    [t('reports.netProfit'), data.netProfit, true],
  ];

  const printIt = () =>
    void printDocument(
      t('reports.pl'),
      `<h1>${escapeHtml(t('reports.pl'))}</h1>
       <div class="sub">${escapeHtml(settings.storeNameEn)} · ${escapeHtml(range.from.slice(0, 10))} → ${escapeHtml(range.to.slice(0, 10))}</div>
       <table><tbody>${lines
         .map(
           ([k, v, strong]) =>
             `<tr><td${strong ? ' style="font-weight:700"' : ''}>${escapeHtml(k)}</td><td class="num"${strong ? ' style="font-weight:700"' : ''}>${escapeHtml(money(v))}</td></tr>`,
         )
         .join('')}</tbody></table>`,
    );

  return (
    <div className="col">
      <ExportBar xlsx={`/reports/export/profit-and-loss.xlsx?from=${range.from}&to=${range.to}`} onPrint={printIt} />
      <div className="grid c3">
        <Kpi label={t('reports.grossProfit')} value={money(data.grossProfit)} />
        <Kpi label={t('reports.expensesExVat')} value={money(data.expensesExVat)} detail={`${t('common.vat')} ${money(data.expenseVat)}`} />
        <Kpi label={t('reports.netProfit')} value={money(data.netProfit)} accent detail={`${data.marginPercent}%`} />
      </div>
      <p className="small muted">{t('reports.profitNote')}</p>
      <div className="card">
        <div className="table-wrap">
          <table>
            <tbody>
              {lines.map(([k, v, strong]) => (
                <tr key={k}>
                  <td className={strong ? 'strong' : ''}>{k}</td>
                  <td className={`num tnum ${strong ? 'strong' : ''}`}>{money(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {data.expenseRows.length > 0 ? (
        <div className="card flush">
          <div style={{ padding: 'var(--s-4) var(--s-5) 0' }}>
            <h3>{t('reports.expenses')}</h3>
          </div>
          <div className="table-wrap">
            <table className="cards">
              <tbody>
                {data.expenseRows.map((r) => (
                  <tr key={String(r.category)}>
                    <td className="strong">{String(r.category)}</td>
                    <td className="num tnum">{money(Number(r.total))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
